import '@/css/classes/modal.scss';
import svgSquareXmark from '@static/icons/square-xmark.svg?raw';
import svgHourglassHalf from '@static/icons/hourglass-half.svg?raw';
import svgCheckSquare from '@static/icons/check-square.svg?raw'

import { assertEquals } from '@/js/util/asserts.js';
import {genId, isAnyInputFocused} from '@/js/util.js';


export class Modal {
    static modals = new Set();

    constructor(title) {
        this.id = 'modal_'+genId();
        this.title = title;
        this._onclose_callback = null;
        this._afterclose_callbacks = [];
        this._is_setup = false;
        this.allow_close = true;
        this.verbose_close_button = true;
        this.close_button_text = 'Cancel';
        this.wide = false;
        this.extrawide = false;
        this.fullscreen = false;
        this.body_padding = true;
        this.anti_clickthrough_timeout_ms = 200;
        this.is_showing = false;

        Modal.modals.add(this);
    }

    _temporarilyDisableClicks() {
        if(!this.content_elem) { return; }
        this.content_elem.classList.add('disable-clicks-temporarily');

        setTimeout(() => {
            if(!this.content_elem) { return; }
            this.content_elem.classList.remove('disable-clicks-temporarily');
        }, this.anti_clickthrough_timeout_ms);
    }

    _setup() {
        this.overlay_elem = document.createElement('div');
        this.overlay_elem.classList.add('di-modal-screen-overlay');
        this.overlay_elem.setAttribute('active', 'false');
        this.overlay_elem.id = this.id;

        this.content_elem = document.createElement('div');
        this.content_elem.classList.add('di-modal-content', 'sizing-container');
        if(this.fullscreen) {
            this.content_elem.classList.add('di-modal-fullscreen');
        } else if(this.extrawide) {
            this.content_elem.classList.add('di-modal-extrawide');
        } else if(this.wide) {
            this.content_elem.classList.add('di-modal-wide');
        }
        this.overlay_elem.appendChild(this.content_elem);

        this.heading_elem = document.createElement('div');
        this.heading_elem.classList.add('di-modal-heading');

        this.heading_elem_left = document.createElement('div');
        this.heading_elem_left.classList.add('d-flex', 'gap-2');
        this.heading_elem.appendChild(this.heading_elem_left);
        this.heading_elem_right = document.createElement('div');
        this.heading_elem_right.classList.add('d-flex', 'gap-2');
        this.heading_elem.appendChild(this.heading_elem_right);

            this.title_elem = document.createElement('span');
            this.title_elem.classList.add('di-modal-title');
            this.title_elem.innerText = this.title;
            this.heading_elem_left.appendChild(this.title_elem);

            if(this.allow_close) {
                this.close_btn = document.createElement('span');
                if(this.verbose_close_button) {
                    this.close_btn.classList.add('btn', 'btn-sm', 'btn-warning', 'd-inline-flex', 'align-items-center', 'gap-1', 'di-modal-close-button-verbose');
                    this.close_btn.innerHTML = `${this.close_button_text} <svg class="fa-icon">${svgSquareXmark}</svg>`;
                } else {
                    this.close_btn.classList.add('di-modal-close-button');
                    this.close_btn.innerText = '×';
                }
                this.close_btn.addEventListener('click', () => {
                    if(this._onclose_callback !== null) {
                        this._onclose_callback();
                    } else {
                        this.remove();
                    }
                });
                this.heading_elem_right.appendChild(this.close_btn);
            }

        this.content_elem.appendChild(this.heading_elem);

        this.body_elem = document.createElement('div');
        this.body_elem.classList.add('di-modal-body');
        if(!this.body_padding) { this.body_elem.classList.add('di-modal-body-no-padding'); }
        this.content_elem.appendChild(this.body_elem);

        document.body.appendChild(this.overlay_elem);
        this._is_setup = true;
        updateBodyScrollClass();
        this._temporarilyDisableClicks();
    }

    setTitle(new_title) {
        if(!this._is_setup) { this._setup(); }
        this.title_elem.innerText = new_title;
        this.title = new_title;
    }

    clearBody() {
        if(!this._is_setup) { return; }
        this.body_elem.replaceChildren();
    }

    appendNode(node) {
        if(!this._is_setup) { this._setup(); }
        this.body_elem.appendChild(node);
    }

    appendHtml(html) {
        if(!this._is_setup) { this._setup(); }
        const node = document.createElement('div');
        node.innerHTML = html;
        this.appendNode(node);
    }

    prependNode(node) {
        if(!this._is_setup) { this._setup(); }
        const first_child = this.body_elem.firstChild;
        if(first_child) {
            this.body_elem.insertBefore(node, first_child);
        } else {
            this.body_elem.appendChild(node);
        }
    }

    prependHtml(html) {
        if(!this._is_setup) { this._setup(); }
        const node = document.createElement('div');
        node.innerHTML = html;
        this.prependNode(node);
    }

    show() {
        if(!this._is_setup) { this._setup(); }
        this.overlay_elem.style.display = 'block';
        this.overlay_elem.setAttribute('active', 'true');
        this.is_showing = true;
        updateBodyScrollClass();
        this._temporarilyDisableClicks();
    }

    hide() {
        if(!this._is_setup) { return; }
        this.overlay_elem.style.display = 'none';
        this.overlay_elem.setAttribute('active', 'false');
        this.is_showing = false;
        updateBodyScrollClass();
    }

    remove() {
        if(!this._is_setup) { return; }
        Modal.modals.delete(this);
        this.clearBody();
        this.overlay_elem.remove();
        this.overlay_elem = null;
        this.content_elem = null;
        this.heading_elem = null;
        this.title_elem = null;
        this.close_btn = null;
        this.body_elem = null;
        this._is_setup = false;
        this.is_showing = false;
        updateBodyScrollClass();
        for(const callback of this._afterclose_callbacks) { callback(); }
    }

    onClose(callback) {
        // If you use this method, you will need to call modal.close or modal.remove yourself
        assertEquals(this._onclose_callback, null, 'Modal _onclose_callback already set');
        this._onclose_callback = callback;
    }

    afterClose(callback) {
        this._afterclose_callbacks.push(callback);
    }

    showCloseButton() {
        if(!this.close_btn) { return; }
        this.close_btn.classList.remove('hidden');
    }

    hideCloseButton() {
        if(!this.close_btn) { return; }
        this.close_btn.classList.add('hidden');
    }
}

export class Button {
    constructor(text, classes) {
        this.text = text;

        const elem = document.createElement('button');
        elem.classList.add('di-button', 'btn', ...classes);
        const has_light_background = classes.some((classname) => classname.includes('btn-outline-'));

        const loading_spinner = document.createElement('div');
        loading_spinner.innerHTML = svgHourglassHalf;
        loading_spinner.classList.add('flex-center', 'loading-spinner-container', 'hidden');
        elem.appendChild(loading_spinner);

        const success_checkmark = document.createElement('div');
        success_checkmark.innerHTML = svgCheckSquare;
        success_checkmark.classList.add('flex-center', 'hidden');
        elem.appendChild(success_checkmark);

        const button_text = document.createElement('div');
        button_text.textContent = this.text
        elem.appendChild(button_text);

        this.elem = elem;
        this._text_elem = button_text;
        this._loading_spinner_elem = loading_spinner;
        this._success_checkmark_elem = success_checkmark;
    }

    onClick(callback) {
        this.elem.addEventListener('click', callback);
    }

    setText(new_text) {
        this._text_elem.textContent = new_text;
    }

    disable() {
        this.elem.disabled = true;
    }

    enable() {
        this.elem.disabled = false;
    }

    startLoading() {
        this._loading_spinner_elem.classList.remove('hidden');
    }

    stopLoading() {
        this._loading_spinner_elem.classList.add('hidden');
    }

    startSuccess() {
        this._success_checkmark_elem.classList.remove('hidden');
    }

    stopSuccess() {
        this._success_checkmark_elem.classList.add('hidden');
    }

    flashSuccess() {
        this.startSuccess();
        setTimeout(() => {
            this.stopSuccess();
        }, 1000);
    }
}

export class ProgressBar {
    constructor(total, classes) {
        this.current = 0;
        this.total = total;

        const elem = document.createElement('div');
        elem.classList.add('di-progressbar', ...classes);

        const inner = document.createElement('div');
        inner.classList.add('di-progressbar-inner');
        elem.appendChild(inner);

        this.elem = elem;
        this.elem_inner = inner;
        this.render();
    }

    render() {
        const percent = Math.min(Math.ceil((this.current / this.total) * 100), 100);
        this.elem_inner.style.width =  percent+'%';
        this.elem_inner.textContent = percent+'%';
    }

    increment(amount_to_increment_by) {
        this.current += (amount_to_increment_by ?? 1);
        this.render();
    }

    decrement(amount_to_increment_by) {
        this.current -= (amount_to_increment_by ?? 1);
        this.render();
    }

    setCurrent(new_current) {
        this.current = new_current;
        this.render();
    }

    setTotal(new_total) {
        this.total = new_total;
        this.render();
    }
}

export class ConfirmationModal extends Modal {
    constructor(title, promptHtml, confirm_button_text) {
        super(title);
        this.promptHtml = promptHtml ?? '';
        this._onconfirm_callback = null;
        this._confirm_enabled = true;
        this.confirm_button_text = confirm_button_text ?? 'Confirm';
        this.confirmation_button = null;
        this._keydown_handler = null;
    }

    _setup() {
        super._setup();

        const inner_body_elem = document.createElement('div');
        inner_body_elem.classList.add('di-inner-body');
        this.appendNode(inner_body_elem);

        this.confirmation_button = new Button(this.confirm_button_text, ['btn-success', 'di-modal-confirmation-button']);
        this.appendNode(this.confirmation_button.elem);

        this.body_elem = inner_body_elem;

        if(this.promptHtml !== '') { this.appendHtml(this.promptHtml); }

        this.confirmation_button.elem.addEventListener('click', () => {
            if(this._confirm_enabled && this._onconfirm_callback) { this._onconfirm_callback(); }
        });

        this._keydown_handler = (event) => {
            if(event.key !== 'Enter') { return; }
            if(!this.is_showing) { return; }
            if(numOpenModals() > 1) { return; }
            if(isAnyInputFocused()) { return; }

            event.preventDefault();
            if(this._confirm_enabled && this._onconfirm_callback) { this._onconfirm_callback(); }
        };

        document.addEventListener('keydown', this._keydown_handler);
    }

    remove() {
        if(this._keydown_handler) {
            document.removeEventListener('keydown', this._keydown_handler);
            this._keydown_handler = null;
        }
        super.remove();
    }

    onConfirm(callback) {
        this._onconfirm_callback = callback;
    }

    disableConfirmationButton() {
        if(!this._is_setup) { this._setup(); }
        this._confirm_enabled = false;
        this.confirmation_button.disable();
    }

    enableConfirmationButton() {
        if(!this._is_setup) { this._setup(); }
        this._confirm_enabled = true;
        this.confirmation_button.enable();
    }

    startLoading() {
        if(!this._is_setup) { this._setup(); }
        this.confirmation_button.startLoading();
    }

    stopLoading() {
        if(!this._is_setup) { this._setup(); }
        this.confirmation_button.stopLoading();
    }

    setConfirmationButtonText(new_text) {
        if(!this._is_setup) { this._setup(); }
        this.confirmation_button.setText(new_text);
    }
}

export class InfoModal extends ConfirmationModal {
    constructor(promptHtml, title, confirm_button_text) {
        super(title ?? 'Info', promptHtml, confirm_button_text ?? 'Dismiss');
        this.allow_close = false;
    }

    _setup() {
        super._setup();
        this.overlay_elem.style.zIndex = '400';
        this.onConfirm(() => this.remove());
    }
}

export class ErrorModal extends ConfirmationModal {
    constructor(promptHtml, title, confirm_button_text) {
        super(title ?? 'Error', promptHtml, confirm_button_text ?? 'Dismiss');
        this.allow_close = false;
    }

    _setup() {
        super._setup();
        this.overlay_elem.style.zIndex = '500';
        this.onConfirm(() => this.remove());
    }
}

export class InputModal extends ConfirmationModal {
    constructor(title, promptHtml, confirm_button_text, inputPlaceholder) {
        super(title, promptHtml, confirm_button_text);
        this.inputPlaceholder = inputPlaceholder ?? '';
        this.input_elem = null;
    }

    _setup() {
        super._setup();

        const input = document.createElement('input');
        input.classList.add('di-inputmodal-input');
        input.placeholder = this.inputPlaceholder;
        this.input_elem = input;
        if(this.body_elem.lastElementChild) {
            this.body_elem.lastElementChild.before(input);
        } else {
            this.body_elem.appendChild(input);
        }
    }

    onConfirm(callback) {
        super.onConfirm(() => {
            callback(this.input_elem.value);
        });
    }
}

export function numOpenModals() {
    return Array.from(Modal.modals).filter((modal) => modal.is_showing).length;
}

export function modalIsOpen() {
    return Array.from(Modal.modals).some(modal => modal.is_showing);
}

export function removeAllModals() {
    for(const modal of Modal.modals) { modal.remove(); }
    updateBodyScrollClass();
}

function updateBodyScrollClass() {
    if(modalIsOpen()) {
        document.body.classList.add('no-scroll');
        return;
    }

    document.body.classList.remove('no-scroll');
}

export const awaitConfirmation = (title, prompt) => {
    return new Promise((resolve, reject) => {
        const confirmation_popup = new ConfirmationModal(title ?? 'Confirm', prompt ?? '');

        confirmation_popup.onConfirm(async () => {
            confirmation_popup.remove();
            resolve(true);
        });

        confirmation_popup.onClose(async () => {
            confirmation_popup.remove();
            resolve(false);
        });

        confirmation_popup.show();
    });
};